<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row v-if="!isReadOnly" align="center" class="mb-4">
      <v-col cols="12" class="d-flex align-center" style="gap: 10px">
        <v-spacer></v-spacer>
        <v-btn class="accent white--text" @click="openVisibilityDialog">
          <v-icon left small>mdi-cog-outline</v-icon>
          Configure
        </v-btn>
        <v-btn class="accent white--text" @click="uploadDialog = true">
          <v-icon left small>mdi-upload</v-icon>
          Upload
        </v-btn>
        <v-btn :loading="downloadLoader" class="accent white--text" @click="downloadTemplate">
          <v-icon left small>mdi-download</v-icon>
          Download Template
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="visibleRunHeaders"
            :items="runs"
            :loading="loading"
            fixed-header
            item-key="id"
          >
            <template v-slot:no-data>
              <div class="text-center">No scheduler runs found</div>
            </template>

            <template #[`item.start_date`]="{ item }">
              {{ formatDate(item.start_date) }}
            </template>

            <template #[`item.inserted_at`]="{ item }">
              {{ formatDateTime(item.inserted_at) }}
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip
                small
                :color="statusColor(item.status)"
                :text-color="statusTextColor(item.status)"
              >
                <v-icon v-if="item.status === 'running'" left x-small
                  >mdi-loading mdi-spin</v-icon
                >
                {{ item.status }}
              </v-chip>
            </template>

            <template #[`item.total_entries`]="{ item }">
              {{ item.total_entries || "—" }}
            </template>

            <template #[`item.error_message`]="{ item }">
              <span v-if="item.error_message" class="red--text caption">{{
                item.error_message
              }}</span>
              <span v-else class="grey--text">—</span>
            </template>

            <template #[`item.schedules`]="{ item }">
              <v-btn
                icon
                small
                :disabled="item.status !== 'success'"
                @click="goToEntries(item)"
              >
                <v-icon small>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <template #[`item.actions`]="{ item }">
              <v-btn
                icon
                small
                :disabled="item.status !== 'success'"
                @click="deleteConfirmation(item)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="uploadDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Run Webinar Scheduler
          <v-spacer />
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5">
          <v-form ref="uploadForm" lazy-validation>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model.number="upload.title"
                  label="Title *"
                  outlined
                  dense
                  hide-details="auto"
                  min="1"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-file-input
                  v-model="upload.file"
                  label="Input CSV File *"
                  outlined
                  dense
                  hide-details="auto"
                  accept=".csv"
                  prepend-icon=""
                  :rules="[rules.required]"
                  show-size
                />
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="datePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formattedStartDate"
                      label="Start Date *"
                      outlined
                      dense
                      hide-details="auto"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[rules.required]"
                      prepend-inner-icon="mdi-calendar"
                    />
                  </template>
                  <v-date-picker
                    v-model="upload.start_date"
                    :allowed-dates="allowedDates"
                    :min="new Date().toISOString().substr(0, 10)"
                    @input="datePicker = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="6">
                <v-text-field
                  v-model.number="upload.max_per_slot"
                  label="Max sessions per Slot *"
                  outlined
                  dense
                  hide-details="auto"
                  type="number"
                  min="1"
                  :rules="[rules.required, rules.nonZero]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn
            class="accent white--text"
            :loading="submitting"
            :disabled="submitting"
            @click="submitRun"
          >
            <v-icon left small>mdi-play</v-icon>
            Run Scheduler
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="visibilityDialog" max-width="560" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Webinar Visibility
          <v-spacer />
          <v-btn small icon :disabled="savingVisibility" @click="visibilityDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5" style="max-height: 60vh">
          <p class="caption grey--text text--darken-1 mb-3">
            Students only see webinars for courses handled by the partners ticked
            below. Unticking a partner hides its webinars from every student.
          </p>

          <div v-if="loadingVisibility" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="36" width="3" />
          </div>

          <div
            v-else-if="!visibilityConfigs.length"
            class="text-center py-8 grey--text"
          >
            No handled-by partners found
          </div>

          <template v-else>
            <div class="d-flex align-center mb-2">
              <v-text-field
                v-model="visibilitySearch"
                label="Search partner"
                outlined
                dense
                hide-details
                clearable
                prepend-inner-icon="mdi-magnify"
              />
            </div>

            <v-checkbox
              v-for="config in filteredVisibilityConfigs"
              :key="config.partner_id"
              v-model="config.visible"
              :label="visibilityLabel(config)"
              dense
              hide-details
              class="mt-2"
            />
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn text color="grey darken-1" :disabled="savingVisibility" @click="visibilityDialog = false">
            Cancel
          </v-btn>
          <v-btn
            class="accent white--text"
            :loading="savingVisibility"
            :disabled="loadingVisibility || !visibilityConfigs.length"
            @click="saveVisibility"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <confirmationDialog
        v-if="confirmationDialog"
        confirmationText="Are you sure you want to delete this scheduler run and all its schedule entries? This action cannot be undone."
        @closeDialog="confirmationDialog = false"
        @saveDialog="deleteEntry()"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import confirmationDialog from "@/components/confirmationDialog.vue";
import { webinarRunHeaders } from "@/constants/headers";

export default {
  components: { confirmationDialog },
  data() {
    return {
      loading: false,
      runs: [],
      rules,
      downloadLoader: false,
      uploadDialog: false,
      submitting: false,
      datePicker: false,
      upload: {
        title: "",
        file: null,
        start_date: null,
        max_per_slot: 3,
      },
      runHeaders: webinarRunHeaders,
      visibilityDialog: false,
      loadingVisibility: false,
      savingVisibility: false,
      visibilitySearch: "",
      visibilityConfigs: [],
      confirmationDialog: false,
      selectedEntry: {},
    };
  },

  computed: {
    isReadOnly() {
      return this.$store.state.currentUser?.role === "Attendance Verifier";
    },
    visibleRunHeaders() {
      if (!this.isReadOnly) return this.runHeaders;
      return this.runHeaders.filter((h) => h.value !== "actions");
    },
    filteredVisibilityConfigs() {
      const query = (this.visibilitySearch || "").trim().toLowerCase();
      if (!query) return this.visibilityConfigs;
      return this.visibilityConfigs.filter((config) =>
        this.visibilityLabel(config).toLowerCase().includes(query),
      );
    },
    formattedStartDate() {
      if (!this.upload.start_date) return "";
      const [y, m, d] = this.upload.start_date.split("-");
      return `${d}-${m}-${y}`;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Webinar Scheduler" }]);
    await this.fetchRuns();
  },

  methods: {
    async fetchRuns() {
      this.loading = true;
      try {
        const res = await this.$axios.get("/scheduler/runs");
        this.runs = res.data.data || [];
      } catch (e) {
        console.log(e);
      } finally {
        this.loading = false;
      }
    },

    visibilityLabel(config) {
      if (config.partner_code) {
        return `${config.partner_name} (${config.partner_code})`;
      }
      return config.partner_name;
    },

    openVisibilityDialog() {
      this.visibilitySearch = "";
      this.visibilityDialog = true;
      this.fetchVisibilityConfigs();
    },

    async fetchVisibilityConfigs() {
      this.loadingVisibility = true;
      try {
        const res = await this.$axios.get("/scheduler/webinar_visibility");
        this.visibilityConfigs = (res.data.data || []).map((config) => ({
          ...config,
          visible: config.visible !== false,
        }));
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to load webinar visibility";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
        this.visibilityConfigs = [];
      } finally {
        this.loadingVisibility = false;
      }
    },

    async saveVisibility() {
      this.savingVisibility = true;
      try {
        await this.$axios.put("/scheduler/webinar_visibility", {
          configs: this.visibilityConfigs.map((config) => ({
            partner_id: config.partner_id,
            visible: config.visible,
          })),
        });
        this.$store.commit("showSnackbar", {
          message: "Webinar visibility updated",
          color: "success",
        });
        this.visibilityDialog = false;
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Failed to update webinar visibility";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.savingVisibility = false;
      }
    },

    async submitRun() {
      if (!this.$refs.uploadForm.validate()) return;
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append("title", this.upload.title);
        fd.append("input_file", this.upload.file);
        fd.append("start_date", this.upload.start_date);
        fd.append("max_per_slot", this.upload.max_per_slot);

        const response = await this.$axios.post("/scheduler/run", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
        });

        const contentType = response.headers["content-type"] || "";
        if (
          contentType.includes("text/csv") ||
          contentType.includes("application/octet-stream")
        ) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "scheduler_errors.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);

          this.$store.commit("showSnackbar", {
            message: "Errors found — check the downloaded CSV",
            color: "warning",
          });
        } else {
          this.$store.commit("showSnackbar", {
            message: "Scheduler run submitted successfully",
            color: "success",
          });
          this.closeUploadDialog();
          await this.fetchRuns();
        }
      } catch (e) {
        if (e.response?.data instanceof Blob) {
          try {
            const text = await e.response.data.text();
            const json = JSON.parse(text);
            const msg = json.error || json.message || "Scheduler run failed";
            this.$store.commit("showSnackbar", {
              message: msg,
              color: "error",
            });
          } catch {
            this.$store.commit("showSnackbar", {
              message: "Scheduler run failed",
              color: "error",
            });
          }
        } else {
          const msg =
            e.response?.data?.error ||
            e.response?.data?.message ||
            "Scheduler run failed";
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        }
      } finally {
        this.submitting = false;
      }
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.upload = { file: null, start_date: null, max_per_slot: 3 };
      this.$refs.uploadForm?.reset();
    },

    allowedDates(val) {
      const [year, month, day] = val.split("-").map(Number);
      const date = new Date(year, month - 1, day);
      return date.getDay() === 5;
    },

    goToEntries(run) {
      this.$router.push(`/webinar-scheduler/${run.id}/entries`);
    },

    downloadTemplate() {
      this.downloadLoader = true;
      this.$axios
        .get("/scheduler/download_webinar_template", { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "webinar_scheduler_template.csv");
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          this.downloadLoader = false;
        })
        .catch((e) => {
          this.downloadLoader = false;
          const msg =
            e.response?.data?.error ||
            e.response?.data?.message ||
            "Failed to download template";
          this.$store.commit("showSnackbar", { message: msg, color: "error" });
        });
    },

    formatDate(dateStr) {
      if (!dateStr) return "—";
      const [y, m, d] = dateStr.split("-");
      return `${d}-${m}-${y}`;
    },

    formatDateTime(dt) {
      if (!dt) return "—";
      const d = new Date(dt);
      if (isNaN(d)) return dt;
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}-${pad(
        d.getMonth() + 1,
      )}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    statusColor(status) {
      return (
        {
          pending: "grey lighten-2",
          running: "blue lighten-4",
          success: "green lighten-4",
          failed: "red lighten-4",
        }[status] || "grey lighten-2"
      );
    },

    statusTextColor(status) {
      return (
        {
          pending: "grey darken-2",
          running: "blue darken-2",
          success: "green darken-2",
          failed: "red darken-2",
        }[status] || "grey darken-2"
      );
    },

    deleteConfirmation(entry) {
      this.confirmationDialog = true;
      this.selectedEntry = entry;
    },

    deleteEntry() {
      this.$axios
        .delete(`/scheduler/runs/${this.selectedEntry.id}`)
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Scheduler run deleted successfully",
            color: "success",
          });
          this.confirmationDialog = false;
          this.selectedEntry = {};
          this.fetchRuns();
        })
        .catch((e) => {
          const msg =
            e.response?.data?.errors ||
            e.response?.data?.message ||
            "Failed to delete scheduler run";
          this.$store.commit("showSnackbar", {
            message: msg,
            color: "error",
          });
        });
    },
  },
};
</script>
